import { dedupeTaxOptions } from '../tax-options.util';

export interface CompanyTaxCatalogEntry {
  id: number;
  [key: string]: unknown;
}

/**
 * CEC-657: the line grid can hand a saved tax back as a bare numeric id instead of an object
 * (`tax?.id || tax?.taxId || tax`). Resolving it against the full company tax catalogue — instead
 * of requiring the grid's own shape to already be an object — re-injects the selection instead of
 * silently dropping it (which then omitted `taxIds` on save and let the server clear the tax).
 */
export function resolveTaxFromCatalog(
  companyTaxes: CompanyTaxCatalogEntry[] | null | undefined,
  taxId: number,
): CompanyTaxCatalogEntry | undefined {
  return (companyTaxes || []).find((tax) => Number(tax?.id) === taxId);
}

/**
 * CEC-657: the server now distinguishes an absent `taxIds` (leave untouched) from an explicit
 * `[]` (clear). The single-select tax form control must always resolve to an explicit array —
 * never `null`/omitted — so clearing the dropdown and saving actually clears the line's taxes.
 */
export function resolveExplicitTaxIds(formTaxId: unknown): number[] {
  return formTaxId ? [formTaxId as number] : [];
}

/**
 * CEC-687: the taxes a line drawer offers. The screen never discards the tax a line already has.
 * The drawers used to blank it when the line's product was not in the catalogue (archived) or when
 * the tax belonged to another company, and saving then sent `taxIds: []`, which the server reads as
 * "clear" — 610 production lines would have lost their tax silently. Now the line's own tax is always
 * offered (from the full catalogue, or with a generic label if nobody knows it) and sent as is: the
 * server decides, and if it rejects the tax the drawer shows why.
 */
export function resolveOfferedTaxes(
  companyTaxes: CompanyTaxCatalogEntry[] | null | undefined,
  currentTaxId: unknown,
  fullCatalog?: CompanyTaxCatalogEntry[] | null,
): CompanyTaxCatalogEntry[] {
  const offered = [...(companyTaxes || [])];
  const taxId = Number(currentTaxId || 0);
  if (taxId && !offered.some((tax) => Number(tax?.id) === taxId)) {
    offered.push(resolveTaxFromCatalog(fullCatalog, taxId) ?? { id: taxId, name: `Impuesto ${taxId}` });
  }

  // CEC-689: cada impuesto una sola vez por nombre y porcentaje. Si la línea ya tiene uno de un grupo
  // repetido, es ese el que se ofrece: no se le cambia sin querer ni sale dos veces.
  return dedupeTaxOptions(offered, null, taxId);
}
